import { PlayDataManagerAPIClient } from './PlayDataManagerAPIClient';
import { IQuestionAPIClient, ITeamAPIClient, ITeamMemberAPIClient, ITeamAnswerAPIClient, IGameSessionAPIClient } from '../gamesession/interfaces';
import { IQuestion, IChoice } from '../../Models/IQuestion';
import { IHostTeamAnswers, IHostTeamAnswersQuestion, IHostTeamAnswersHint, IHostTeamAnswersConfidence, IHostTeamAnswersResponse, IHostTeamAnswersPerPhase, ITeam } from '../../Models';
import { Environment } from '../interfaces/IBaseAPIClient';
import { IGameSession } from '../../Models/IGameSession';
import { BackendAnswer, Answer, NumericAnswer, MultiChoiceAnswer, AnswerFactory, AnswerType } from '../../Models/AnswerClasses';
import { GameSessionState, ConfidenceLevel } from '../../AWSMobileApi';

export enum HTTP {
  Post = "POST",
}

export enum IPhase {
  ONE = 'phase1',
  TWO = 'phase2'
}

export class HostDataManagerAPIClient extends PlayDataManagerAPIClient {
  protected questionAPIClient: IQuestionAPIClient;
  protected teamAPIClient: ITeamAPIClient;
  protected teamMemberAPIClient: ITeamMemberAPIClient;
  protected teamAnswerAPIClient: ITeamAnswerAPIClient;
  protected createTeamSubscription: any;
  protected updateTeamSubscription: any;
  private teamResyncInFlight: boolean;
  private teamsPushedDuringResync: ITeam[];
  private hostTeamAnswers: IHostTeamAnswers;
  private createTeamAnswerSubscription: any;
  private updateTeamAnswerSubscription: any;
  private noResponseCharacter: string;
  private lambdaHintEndpoint: string;

  constructor (
    env: Environment,
    gameSessionAPIClient: IGameSessionAPIClient,
    questionAPIClient: IQuestionAPIClient,
    teamAPIClient: ITeamAPIClient,
    teamMemberAPIClient: ITeamMemberAPIClient,
    teamAnswerAPIClient: ITeamAnswerAPIClient,
  ) {
    super(env, gameSessionAPIClient);
    this.questionAPIClient = questionAPIClient;
    this.teamAPIClient = teamAPIClient;
    this.teamMemberAPIClient = teamMemberAPIClient;
    this.teamAnswerAPIClient = teamAnswerAPIClient;
    this.hostTeamAnswers = {questions:[]};
    this.teamResyncInFlight = false;
    this.teamsPushedDuringResync = [];
    this.noResponseCharacter = '…';
    this.lambdaHintEndpoint = `https://yh5ionr9rg.execute-api.us-east-1.amazonaws.com/groupHints/groupHints`;
  }

  // get game session from backend and build host team answers object for use in Victory graphs
  async init(gameSessionId: string){
    this.gameSessionId = gameSessionId;
    this.gameSession = await this.gameSessionAPIClient.getGameSession(this.gameSessionId).then(
      (gameSession: IGameSession) => {
        this.gameSession = gameSession;
        if (gameSession.currentState !== GameSessionState.TEAMS_JOINING)
          this.initHostTeamAnswers(gameSession);
        return gameSession;
      }
    );
  }

  cleanupSubscription() {
    // The subscribe* API client methods are async, so these fields hold Promise<Subscription>,
    // not Subscription — a direct `.unsubscribe` check is always falsy and silently leaks the
    // socket. Resolve first (also correct when cleanup races a subscribe still connecting).
    [
      this.gameSessionSubscription,
      this.createTeamSubscription,
      this.updateTeamSubscription,
      this.createTeamAnswerSubscription,
      this.updateTeamAnswerSubscription,
    ].forEach((subscription) => {
      Promise.resolve(subscription)
        .then((resolved: any) => resolved?.unsubscribe?.())
        .catch((error) => console.error('Error: failed to unsubscribe', error));
    });
  }

  //subscribe to created teams, when players are joining in the lobby
  subscribeToCreateTeam(callback: (updatedGameSession: IGameSession | null) => void): void {
    if (!this.gameSessionId) {
      console.error('Error: Invalid game session id');
      return;
    }
  
    this.createTeamSubscription = this.teamAPIClient.subscribeCreateTeam(this.gameSessionId, (team: ITeam) => {
      if (!team) {
        console.error('Error: Invalid team');
        return;
      }
      // Buffer pushes that land mid-resync: resyncTeams' fetch may have been served before this
      // team existed, so replacing the roster with that response alone would drop it again.
      if (this.teamResyncInFlight)
        this.teamsPushedDuringResync.push(team);
      const currentTeams = (this.gameSession as IGameSession)?.teams ?? [];
      // Idempotent: resyncTeams can surface a team the socket later delivers (or redelivers).
      if (currentTeams.some((existing: ITeam) => existing.id === team.id))
        return;
      const newGameSession = { ...this.gameSession as IGameSession, teams: [...currentTeams, team] };
      this.gameSession = newGameSession;
      callback(newGameSession);
    });
  }

  // Reconcile the locally-held roster against the backend. subscribeToCreateTeam only ever appends
  // what the socket delivers, so any onCreateTeam lost while the socket was down (host device
  // backgrounded, a network blip — Amplify reconnects but never replays what it missed) leaves that
  // team invisible for the rest of the session. Refetching is the only way back: a Team is a child
  // record, so creating one does not move GameSession.updatedAt and it can't be used as a cursor.
  // The fetched roster REPLACES the local one (server is authoritative for removals — deleteTeam's
  // own refetch-replace demonstrably recovered a missed team in production on 2026-07-15).
  async resyncTeams(): Promise<{ gameSession: IGameSession; changed: boolean } | null> {
    // Bail until init has loaded a session; triggers can fire before then.
    if (!this.gameSessionId || !this.gameSession)
      return null;
    if (this.teamResyncInFlight)
      return null;
    this.teamResyncInFlight = true;
    this.teamsPushedDuringResync = [];
    try {
      const fetchedGameSession = await this.gameSessionAPIClient.getGameSession(this.gameSessionId);
      if (!fetchedGameSession)
        return null;
      const teamsById = new Map<string, ITeam>();
      (fetchedGameSession.teams ?? []).forEach((team: ITeam) => {
        if (team?.id) teamsById.set(team.id, team);
      });
      this.teamsPushedDuringResync.forEach((team: ITeam) => {
        if (team?.id) teamsById.set(team.id, team);
      });
      const priorIds = ((this.gameSession as IGameSession).teams ?? []).map((team: ITeam) => team.id).sort();
      const nextIds = Array.from(teamsById.keys()).sort();
      const changed = priorIds.length !== nextIds.length || priorIds.some((id, i) => id !== nextIds[i]);
      // Teams only. The host drives currentState itself (it holds no gameSession subscription), so
      // merging the fetched copy wholesale could clobber a just-advanced phase with a staler read.
      const newGameSession = {
        ...(this.gameSession as IGameSession),
        teams: Array.from(teamsById.values()),
      };
      this.gameSession = newGameSession;
      return { gameSession: newGameSession, changed };
    } catch (error) {
      console.error('Error: failed to resync teams', error);
      return null;
    } finally {
      this.teamResyncInFlight = false;
      this.teamsPushedDuringResync = [];
    }
  }


  //subscribe to updated teams, when players' scores are being assigned
  subscribeToUpdateTeam(callback: (updatedGameSession: IGameSession | null) => void): void {
    if (!this.gameSessionId) {
      console.error('Error: Invalid game session id');
      return;
    }
  
    this.updateTeamSubscription = this.teamAPIClient.subscribeUpdateTeam(this.gameSessionId, (team: ITeam) => {
      if (!team) {
        console.error('Error: Invalid team');
        return;
      }
      // Replace by id, or append when the team isn't in the roster — an update can arrive for a
      // team whose create push was lost, and indexing with findIndex's -1 would write teams[-1]
      // and silently drop it. New array either way; the old one may be shared with prior state.
      const currentTeams = (this.gameSession as IGameSession)?.teams ?? [];
      const newTeams = currentTeams.some((t) => t.id === team.id)
        ? currentTeams.map((t) => (t.id === team.id ? team : t))
        : [...currentTeams, team];
      const newGameSession = { ...this.gameSession as IGameSession, teams: newTeams };
      this.gameSession = newGameSession;
      callback(newGameSession);
    });
  }

  async updateTime(newTime: number) {
    if (this.gameSession && this.gameSessionId && this.gameSession.startTime){
      try {  
        await this.gameSessionAPIClient.updateGameSession({id: this.gameSessionId, startTime: newTime.toString()}).then((gameSession: IGameSession) => {
          this.gameSession = gameSession;
        });
      } catch (error) {
        console.log(error);
      }
    }
  }


  async updateGameSession(gameSessionUpdates: any) {
    if (this.gameSession && this.gameSessionId){
      try {  
        await this.gameSessionAPIClient.updateGameSession({id: this.gameSessionId, ...gameSessionUpdates }).then((gameSession: IGameSession) => {
          this.gameSession = gameSession;
        });
      } catch (error) {
        console.log(error);
      }
    }
  }

  async deleteTeam(teamId: string, callback: (gameSession: IGameSession) => void) {
    try {
      await this.teamAPIClient.deleteTeam(teamId).then(() => {
        if (this.gameSession)
          this.gameSessionAPIClient.getGameSession(this.gameSession.id).then((gameSession: IGameSession) => {
            this.gameSession = gameSession;
            callback(gameSession);
          });
    });
    } catch(e) {
      console.log(e)
    }
  }

  // TeamAnswers handling 
  // type guard to check for answer type at runtime (to ensure normAnswer: string[] | number[] is typesafe)
  private isAnswerNumeric (answer: Answer): answer is NumericAnswer {
    return answer instanceof NumericAnswer;
  }

  // type guard to check for answer type at runtime (to ensure normAnswer: string[] | number[] is typesafe)
  private isAnswerMultiChoice (answer: Answer): answer is MultiChoiceAnswer {
    return answer instanceof MultiChoiceAnswer;
  }

  private createAnswerFromBackendData = (ans: any): Answer => {
    const { rawAnswer, answerType } = ans;
    switch (answerType) {
      case AnswerType.NUMBER:
        return AnswerFactory.createAnswer(rawAnswer, answerType, ans.answerPrecision);
      case AnswerType.MULTICHOICE:
        return AnswerFactory.createAnswer(rawAnswer, answerType, undefined, ans.multiChoiceCharacter);
      default:
        return AnswerFactory.createAnswer(rawAnswer, answerType);
    }
  };

  // A team has "answered" this phase once it appears in any real (non "no-response") response
  // bucket. Centralized so the duplicate-answer guard lives in ONE place — shared by
  // processAnswer and decrementNoResponseCount. Answers and hints are final once submitted;
  // only confidence may change after submission (handled in processConfidenceLevel).
  private hasTeamAnswered(teamAnswersQuestion: any, phase: IPhase, teamName: string): boolean {
    return teamAnswersQuestion[phase].responses.some(
      (response: any) =>
        response.multiChoiceCharacter !== this.noResponseCharacter &&
        response.teams.includes(teamName)
    );
  }

  private processAnswer(ans: any, teamAnswersQuestion: any, phase: IPhase, teamName: string) {
    // First answer wins: if this team already answered this phase (e.g. the same identity
    // submitting from a second tab, or a duplicate subscription delivery), ignore the duplicate
    // so it can't inflate the response counts / graph / "players answered" footer.
    if (this.hasTeamAnswered(teamAnswersQuestion, phase, teamName)) {
      return teamAnswersQuestion[phase].responses;
    }
    const answerObj = this.createAnswerFromBackendData(ans.answer);
    let newResponses = [...teamAnswersQuestion[phase].responses];
    if (answerObj) {
      answerObj.normalizeAnswer(answerObj.rawAnswer);
      const existingAnswer = teamAnswersQuestion[phase].responses.find((response: any) => {
        if (answerObj.rawAnswer.toString() === response.rawAnswer.toString())
          return true;
        if (this.isAnswerNumeric(answerObj)) {
          return answerObj.isEqualTo(response.normAnswer as number[]);
        }
        return answerObj.isEqualTo(response.normAnswer as string[]);
      });
      if (existingAnswer) {
        newResponses = newResponses.map((response: any) => {
          if (response === existingAnswer) {
            return {
              ...response,
              count: response.count + 1,
              teams: [...response.teams, teamName]
            };
          }
          return response;
        });
      } else {
        const newAnswer = {
          normAnswer: answerObj.normAnswer as string[] | number[],
          rawAnswer: answerObj.rawAnswer,
          count: 1,
          isCorrect: ans.isCorrect,
          multiChoiceCharacter: this.isAnswerMultiChoice(answerObj) ? answerObj.multiChoiceCharacter : '',
          teams: [teamName]
        };
        newResponses.push(newAnswer);
      }
      if (this.isAnswerMultiChoice(answerObj))
        newResponses.sort((a: any, b: any) => b.multiChoiceCharacter.localeCompare(a.multiChoiceCharacter));
      else{
        newResponses.sort((a: any, b: any) => a.rawAnswer.localeCompare(b.rawAnswer));
        const noResponse = newResponses.filter((response: any) => response.multiChoiceCharacter === this.noResponseCharacter);
        const otherResponses = newResponses.filter((response: any) => response.multiChoiceCharacter !== this.noResponseCharacter);
        newResponses = [...noResponse, ...otherResponses];
      }
    }
    return newResponses;
  }

  private processConfidenceLevel(ans: BackendAnswer, teamAnswersQuestion: IHostTeamAnswersQuestion, teamName: string){
    let newConfidences: IHostTeamAnswersConfidence[] = []
    if (teamAnswersQuestion[IPhase.ONE].confidences)
      newConfidences = [...teamAnswersQuestion[IPhase.ONE].confidences];
    // step one: remove this team's prior confidence entry from BOTH correctness buckets.
    // Confidence is the one response type that can change after submission, so we always clear the
    // team's previous entry before re-adding. Filtering both sides (not just the current
    // isCorrect side) prevents a correctness flip — or a duplicate submission from a second tab —
    // from leaving the team double-listed across the correct and incorrect buckets.
    newConfidences = newConfidences.map((confidence) => ({
      ...confidence,
      correct: confidence.correct.filter((entry) => entry.team !== teamName),
      incorrect: confidence.incorrect.filter((entry) => entry.team !== teamName),
    }));
    // step two: push the new confidence value into the correct object
    const confidenceLevel = newConfidences.find((confidence: any) => confidence.level === ans.confidenceLevel);
    if (confidenceLevel) {
      if (ans.isCorrect){
        confidenceLevel.correct.push({
          team: teamName,
          rawAnswer: ans.answer.rawAnswer
        });
      } else {
        confidenceLevel.incorrect.push({
          team: teamName,
          rawAnswer: ans.answer.rawAnswer
        });
      }
    // step three: splices in the new confidenceLevel created above into the newConfidences array, replacing the old one
    const matchingIndex = newConfidences.findIndex((confidence: IHostTeamAnswersConfidence) => confidence.level === confidenceLevel.level);
    if (matchingIndex !== -1 && teamAnswersQuestion.phase1.confidences && matchingIndex) 
      newConfidences[matchingIndex] = confidenceLevel;
    }
    return newConfidences;
  }

  private processHint(ans: any, teamAnswersQuestion: any){
    const hints = [...teamAnswersQuestion[IPhase.TWO].hints];
    const hint = JSON.parse(ans.hint);
    if (hint){
      // First hint wins: a hint is final once submitted (like an answer, unlike confidence).
      // Ignore a duplicate hint from a team that already has one (e.g. a second tab sharing the
      // identity) so it can't double-list on the hints graph or in the GPT-hint summary.
      const alreadyHinted = hints.some((existing: any) => existing.teamName === hint.teamName);
      if (!alreadyHinted) {
        hints.push({
          rawHint: hint.rawHint,
          teamName: hint.teamName
        })
      }
    }
    return hints;
  }

  public async processGPTHints(
    hints: IHostTeamAnswersHint[],
    questionText: string,
    correctAnswer: string
  ):Promise<any> {
      try { 
      const attempt = fetch(this.lambdaHintEndpoint, {
          method: HTTP.Post,
          headers: {
              "content-type": "application/json",
              connection: "close",
              "Access-Control-Allow-Origin": "*",
          },
          body: JSON.stringify({
              hints,
              questionText: questionText,
              correctAnswer: correctAnswer
          }),
      })
      .then((response) => {
          if (!response.ok) {
            console.error(response.statusText)
          }
          return response.json();
      })
      
      return attempt;
      } catch (e) {
          console.log(e)
      }
    return "";
  }

  private decrementNoResponseCount(teamAnswersQuestion: any, phase: IPhase, teamName: string) {
    // Only a team's FIRST answer should decrement the outstanding "no response" count. This runs
    // AFTER processAnswer, but processAnswer returns a new array without mutating this question,
    // so on the first answer the team is not yet present here (guard passes → decrement); on a
    // duplicate delivery the team is already present (guard blocks → no double-decrement).
    if (this.hasTeamAnswered(teamAnswersQuestion, phase, teamName)) {
      return;
    }
    const noResponse = teamAnswersQuestion[phase].responses.find((response: any) => response.multiChoiceCharacter === this.noResponseCharacter);
    if (noResponse) {
      noResponse.count = Math.max(noResponse.count - 1,0);
      noResponse.teams = noResponse.teams.filter((team: string) => team !== teamName);
    }
  }

  private ConfidenceLevelDictionary: { [key in ConfidenceLevel]: string } = {
    [ConfidenceLevel.NOT_RATED]: 'Not\nRated',
    [ConfidenceLevel.NOT_AT_ALL]: 'Not\nat all',
    [ConfidenceLevel.KINDA]: 'A\nlittle',
    [ConfidenceLevel.QUITE]: 'Sort\nof',
    [ConfidenceLevel.VERY]: 'Very',
    [ConfidenceLevel.TOTALLY]: 'Totally',
  };

  private buildEmptyHostTeamAnswerConfidences() {
    let confidenceArray = [];
    for (const [key, value] of Object.entries(this.ConfidenceLevelDictionary)) {
      confidenceArray.push({
        level: key as ConfidenceLevel,
        label: value,
        correct: [],
        incorrect: []
      });
    }
    return confidenceArray;
  };

  private buildEmptyHostTeamAnswer(question: IQuestion, teamsCount: number, isShortAnswerEnabled: boolean) {
    const emptyMultiChoiceTeamAnswer = {
      phase1: {
          responses: [
            {
              normAnswer: [],
              rawAnswer: this.noResponseCharacter,
              count: teamsCount,
              isCorrect: false,
              multiChoiceCharacter: this.noResponseCharacter,
              reason: '',
              teams: [],
            },
            ...question.choices.map((choice: IChoice, index: number) => {
              return {
                normAnswer: [choice.text],
                rawAnswer: choice.text,
                count: 0,
                isCorrect: choice.isAnswer,
                multiChoiceCharacter: String.fromCharCode(65 + index),
                reason: choice.reason,
                teams: [],
              }
            })
          ],
          confidences: this.buildEmptyHostTeamAnswerConfidences(),
      },
      phase2: 
        {
          responses: 
          !isShortAnswerEnabled 
          ? [
              {
                normAnswer: [],
                rawAnswer: this.noResponseCharacter,
                count: teamsCount,
                isCorrect: false,
                multiChoiceCharacter: this.noResponseCharacter,
                teams: []
              },
                ...question.choices.map((choice: IChoice, index: number) => {
                  return {
                  normAnswer: [choice.text],
                  rawAnswer: choice.text,
                  count: 0,
                  isCorrect: choice.isAnswer,
                  multiChoiceCharacter: String.fromCharCode(65 + index),
                  reason: choice.reason,
                  teams: []
                  }
                })
            ]
          : [
              {
                normAnswer: [],
                rawAnswer: this.noResponseCharacter,
                count: teamsCount,
                isCorrect: false,
                multiChoiceCharacter: this.noResponseCharacter,
                teams: []
              }
            ],
          hints: []
        }
    }
    emptyMultiChoiceTeamAnswer.phase1.responses.sort((a: any, b: any) => b.multiChoiceCharacter.localeCompare(a.multiChoiceCharacter));
    emptyMultiChoiceTeamAnswer.phase2.responses.sort((a: any, b: any) => b.multiChoiceCharacter.localeCompare(a.multiChoiceCharacter));
    return (emptyMultiChoiceTeamAnswer);
  }

  /* builds out predetermined teamAnswer object that will receive and maintain all student data over the course of a game
   * used for populated Victory graphs rapidly, without continuously having to do find commands through nested data structures etc
   * @params {IGameSesssion} - game session that teacher has created
   * @returns {IHostTeamAnswers} - team answers object that will be mainted throughout the game 
   */
  buildHostTeamAnswers(gameSession: IGameSession){
    let teamAnswers: IHostTeamAnswers = {
      questions: []
    };
    // this generates empty objects for either the multichoice or short answer questions, so that they can be populated later
    if (gameSession.questions.length > 0) {
      teamAnswers.questions = gameSession.questions.map((question) => ({
        questionId: question.id,
        ...this.buildEmptyHostTeamAnswer(question, gameSession.teams.length, question.isShortAnswerEnabled)
      }));
    }
    const numTeams = gameSession.teams.length;
    // this is populating existing answers into the hostTeamAnswers object
    // for each question in the object we just built, loop through all the team answers and populate the object if they apply
    teamAnswers.questions.forEach((question) => {
      gameSession.teams.forEach((team) => {
        team.teamMembers.forEach((teamMember) => {
            teamMember.answers?.forEach((answer) => {
              if (answer?.questionId === question.questionId) {
                const phase: IPhase = answer.currentState === GameSessionState.CHOOSE_CORRECT_ANSWER ? IPhase.ONE : IPhase.TWO;
                // step one: update the responses with the new answer
                question[phase].responses = this.processAnswer(answer, question, phase, team.name);
                // step two: if we're in phase one, check if the player has also provided a confidence and update data accordingly
                if (phase === IPhase.ONE){
                  if (answer?.confidenceLevel)
                    question[phase].confidences = this.processConfidenceLevel(answer, question, team.name);
                }
                // step three: if we're in phase two, check if the player has also provided a hint and update data accordingly
                if (answer?.hint && phase === IPhase.TWO){
                  if (answer?.hint)
                    question[phase].hints = this.processHint(answer, question);
                }
              }
            });
          });
        });
        Object.values(IPhase).forEach((phase) => {
          if (question[phase].responses){
            const teamsAnswered = question[phase].responses
              .reduce<string[]>((acc, response) => {
                if (response.multiChoiceCharacter !== '…') {
                  acc.push(...response.teams);
                }
                return acc;
              }, []);
            
            const teamsUnanswered = gameSession.teams.map((team) => team.name).filter((team) => !teamsAnswered.includes(team));
            const numNoResponses = numTeams - teamsAnswered.length;
            const noResponses = question[phase].responses.find((response: any) => response.multiChoiceCharacter === '…');
            if (noResponses) {
              noResponses.count = numNoResponses ?? 0;
              noResponses.teams = teamsUnanswered;
            }
          }
        });
      });
    return teamAnswers;
  }

  // ensures that featured mistakes that the teacher has selected (sometimes sorted by popularity)
  // are randomized when displayed to students in O(n)
  // Fisher-Yates shuffle per: https://bost.ocks.org/mike/shuffle/
  shuffleSelectedMistakes(updatedResponses: IHostTeamAnswersResponse[]){
    let length = updatedResponses.length, t, i;

    while (length){
      i = Math.floor(Math.random() * length--);
      t = updatedResponses[length];
      updatedResponses[length] = updatedResponses[i];
      updatedResponses[i] = t;
    }
    return updatedResponses;
  }

  updateHostTeamAnswersSelectedMistakes(currentMistakes: any, currentQuestion: IQuestion): IHostTeamAnswers {
    const updatedQuestions = this.hostTeamAnswers.questions.map((question) => {
      if (question.questionId !== currentQuestion.id) {
        return question;
      }
      const updatedResponses = question.phase1.responses.map((response) => {
        const matchingMistake = currentMistakes.find((mistake: any) => mistake.answer === response.rawAnswer);
        if (matchingMistake)
          return {...response, isSelectedMistake: matchingMistake.isSelectedMistake}
        return response;
        }
      );
      return {
        ...question,
        phase1: {
          ...question.phase1,
          responses: updatedResponses,
        },
      };
    });
    this.hostTeamAnswers = {
      ...this.hostTeamAnswers,
      questions: updatedQuestions,
    };
    return this.hostTeamAnswers;
  }

  getHostTeamAnswers() {
    return this.hostTeamAnswers
  }

  getHostTeamAnswersForQuestion(currentQuestionId: string): IHostTeamAnswersQuestion | null {
    const response = this.hostTeamAnswers.questions.find((question) => question.questionId === currentQuestionId);
    if (!response) {
      console.error('Error: Invalid question id');
      return null;
    }
    return response;
  }

  getResponsesForQuestion(currentQuestionId: string, currentPhase: IPhase) {
    const currentQuestion = this.hostTeamAnswers.questions.find((question) => question.questionId === currentQuestionId);
    if (!currentQuestion) {
      console.error('Error: Invalid question id');
      return [];
    }
    let responses = currentQuestion[currentPhase].responses; 
    return responses;
  }

  initHostTeamAnswers(inputGameSession: IGameSession) {
      this.hostTeamAnswers = this.buildHostTeamAnswers(inputGameSession);
    return this.hostTeamAnswers;
  }

  private updateHostTeamAnswerResponses(teamAnswer: BackendAnswer, currentResponses: IHostTeamAnswersResponse[], currentQuestion: IHostTeamAnswersQuestion, phase: IPhase): IHostTeamAnswersResponse[] {
    let newResponses = [...currentResponses];
    newResponses = this.processAnswer(teamAnswer, currentQuestion, phase, teamAnswer.teamName);
    this.decrementNoResponseCount(currentQuestion, phase, teamAnswer.teamName);
    
    if (!teamAnswer) {
      console.error('Error: Invalid team answer');
      return newResponses;
    }
    return newResponses;
  }

  subscribeToCreateTeamAnswer(callback: (createdHostTeamAnswers: IHostTeamAnswers | null) => void): void {
    if (!this.gameSessionId) {
      console.error('Error: Invalid game session id');
      return;
    }
    this.createTeamAnswerSubscription = this.teamAnswerAPIClient.subscribeCreateTeamAnswer(this.gameSessionId, (teamAnswer: BackendAnswer) => {
      if (!teamAnswer) {
        console.error('Error: Invalid team answer');
        return;
      }
      const questionIndex = this.hostTeamAnswers.questions.findIndex((question: any) => question.questionId === teamAnswer.questionId);
      const currentQuestion = this.hostTeamAnswers.questions[questionIndex];
      const phase = teamAnswer.currentState === GameSessionState.CHOOSE_CORRECT_ANSWER ? IPhase.ONE : IPhase.TWO;
      const currentPhase = this.hostTeamAnswers.questions[questionIndex][phase];
      const currentResponses = this.hostTeamAnswers.questions[questionIndex][phase].responses;
      const newResponses = this.updateHostTeamAnswerResponses(teamAnswer, currentResponses, currentQuestion, phase);
  
      // Explicitly working with copies of objects to preserve immutability
      const updatedPhase: IHostTeamAnswersPerPhase = {
        ...currentPhase,
        responses: newResponses
      };
  
      const createdHostTeamAnswers = {
        ...this.hostTeamAnswers,
        questions: this.hostTeamAnswers.questions.map((question, index) => 
          index === questionIndex ? { ...question, [phase]: updatedPhase } : question
        )
      };
      
      this.hostTeamAnswers = createdHostTeamAnswers;
      callback(createdHostTeamAnswers);
    });
  }
  
  subscribeToUpdateTeamAnswer(callback: (updatedHostTeamAnswers: IHostTeamAnswers) => void): void {
    if (!this.gameSessionId) {
      console.error('Error: Invalid game session id');
      return;
    }
    this.updateTeamAnswerSubscription = this.teamAnswerAPIClient.subscribeUpdateTeamAnswer(this.gameSessionId, (teamAnswer: BackendAnswer) => {
      if (!teamAnswer) {
        console.error('Error: Invalid team answer');
        return;
      }
      const questionIndex = this.hostTeamAnswers.questions.findIndex((question: any) => question.questionId === teamAnswer.questionId);
      const currentQuestion = this.hostTeamAnswers.questions[questionIndex];
      const phase = teamAnswer.currentState === GameSessionState.CHOOSE_CORRECT_ANSWER ? IPhase.ONE : IPhase.TWO;
      const currentPhase = currentQuestion[phase];
      let newConfidences = [];
      let newHints = [];
      let newPhase = currentPhase;
      if (phase === IPhase.ONE) {
        newConfidences = this.processConfidenceLevel(teamAnswer, currentQuestion, teamAnswer.teamName);
        newPhase = {...currentPhase, confidences: newConfidences};
      } else {
        newHints = this.processHint(teamAnswer, currentQuestion);
        newPhase = {...currentPhase, hints: newHints};
      }
      const updatedHostTeamAnswers = {
        ...this.hostTeamAnswers,
        questions: this.hostTeamAnswers.questions.map((question, index) =>
          index === questionIndex ? { ...question, [phase]: newPhase } : question
        )
      };
      this.hostTeamAnswers = updatedHostTeamAnswers;
      callback(updatedHostTeamAnswers);
    });
  }
}